import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { uploadAuthorProfilePicture, uploadArticleThumbnail, uploadProgramBanner, uploadBadgeImage, uploadTaskThumbnail } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
  required?: boolean;
  authorId?: string;
  articleId?: string;
  programId?: string;
  badgeKey?: string;
  taskId?: string;
  uploadType?: 'author' | 'article' | 'program' | 'badge' | 'task';
}

export function ImageUpload({ value, onChange, label = "Image", required = false, authorId, articleId, programId, badgeKey, taskId, uploadType = 'author' }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(value || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Update preview URL when value prop changes (for editing existing items)
  React.useEffect(() => {
    setPreviewUrl(value || null);
  }, [value]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPG, PNG, GIF, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);
      
      // Create preview URL
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
      
      // Upload to Firebase Storage
      let downloadURL: string;
      if (uploadType === 'article') {
        downloadURL = await uploadArticleThumbnail(file, articleId);
      } else if (uploadType === 'program') {
        downloadURL = await uploadProgramBanner(file, programId);
      } else if (uploadType === 'badge') {
        downloadURL = await uploadBadgeImage(file, badgeKey);
      } else if (uploadType === 'task') {
        downloadURL = await uploadTaskThumbnail(file, taskId);
      } else {
        downloadURL = await uploadAuthorProfilePicture(file, authorId);
      }
      
      // Update the form with the download URL
      onChange(downloadURL);
      
      toast({
        title: "Image uploaded",
        description: "Profile picture has been uploaded successfully",
      });
    } catch (error: any) {
      logger.error('Error uploading image:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload image. Please try again.",
        variant: "destructive",
      });
      // Reset preview on error
      setPreviewUrl(value || null);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      
      <div className="flex items-start space-x-4">
        {/* Preview Area */}
        <div className="flex-shrink-0">
          {previewUrl ? (
            <div className="relative group">
              <img
                src={previewUrl}
                alt={uploadType === 'article' ? 'Thumbnail preview' : 'Profile preview'}
                className={uploadType === 'article' 
                  ? "w-20 h-12 rounded object-cover border-2 border-slate-200" 
                  : "w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                }
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                disabled={uploading}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className={uploadType === 'article' 
              ? "w-20 h-12 rounded border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50"
              : "w-20 h-20 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50"
            }>
              <ImageIcon className="w-8 h-8 text-slate-400" />
            </div>
          )}
        </div>

        {/* Upload Button */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <Button
            type="button"
            variant="outline"
            onClick={handleUploadClick}
            disabled={uploading}
            className="flex items-center space-x-2"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>{previewUrl ? 'Change Image' : 'Upload Image'}</span>
              </>
            )}
          </Button>
          
          <p className="text-xs text-slate-500 mt-2">
            Supports JPG, PNG, GIF up to 5MB
          </p>
        </div>
      </div>
    </div>
  );
}